import React from 'react';
import { Form, Input, Select, DatePicker, Typography } from 'antd';
import dayjs from 'dayjs';

// The questions of a help desk Request Type form, rendered inside an antd
// Form under name ['answers', questionId]. `formDef` is the structure from
// GET /api/eval-forms/:id (Sections -> Questions -> Options).

const DROPDOWN = ['picklist', 'info_select'];

const allQuestions = (formDef) => (formDef?.Sections || []).flatMap((s) => s.Questions || []);

export default function HelpdeskFormQuestions({ formDef, disabled = false }) {
  if (!formDef) return null;
  const sections = (formDef.Sections || []).filter((s) => (s.Questions || []).length);
  if (!sections.length) return null;
  return (
    <>
      {sections.map((sec) => (
        <div key={sec.ID}>
          {sections.length > 1 && (
            <Typography.Text strong style={{ display: 'block', margin: '4px 0 8px' }}>{sec.Name}</Typography.Text>
          )}
          {(sec.Questions || []).map((q) => (
            <Form.Item
              key={q.ID}
              label={q.QuestionText}
              name={['answers', String(q.ID)]}
              tooltip={q.Guidance || undefined}
              rules={q.IsMandatory ? [{ required: true, message: 'Required' }] : []}
            >
              {DROPDOWN.includes(q.QuestionType) ? (
                <Select
                  allowClear
                  disabled={disabled}
                  options={(q.Options || []).map((o) => ({ value: o.ID, label: o.OptionText }))}
                />
              ) : q.QuestionType === 'date' ? (
                <DatePicker format="YYYY-MM-DD" disabled={disabled} style={{ width: '100%' }} />
              ) : (
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} disabled={disabled} />
              )}
            </Form.Item>
          ))}
        </div>
      ))}
    </>
  );
}

// Form values -> the API's answers list.
export function answersToPayload(formDef, values) {
  return allQuestions(formDef).map((q) => {
    const v = values?.[String(q.ID)];
    if (DROPDOWN.includes(q.QuestionType)) return { question_id: q.ID, option_id: v ?? null };
    if (q.QuestionType === 'date')        return { question_id: q.ID, text: v ? v.format('YYYY-MM-DD') : null };
    return { question_id: q.ID, text: v ?? null };
  });
}

// The API's saved answers -> form values.
export function answersToValues(formDef, saved) {
  const byQ = Object.fromEntries((saved || []).map((a) => [a.QuestionID, a]));
  const out = {};
  allQuestions(formDef).forEach((q) => {
    const a = byQ[q.ID];
    if (!a) return;
    if (DROPDOWN.includes(q.QuestionType)) out[String(q.ID)] = a.OptionID ?? undefined;
    else if (q.QuestionType === 'date')   out[String(q.ID)] = a.AnswerText ? dayjs(a.AnswerText) : undefined;
    else out[String(q.ID)] = a.AnswerText ?? undefined;
  });
  return out;
}

// Read-only text of one answer, for display.
export function answerText(q, saved) {
  const a = (saved || []).find((x) => x.QuestionID === q.ID);
  if (!a) return null;
  if (DROPDOWN.includes(q.QuestionType)) return (q.Options || []).find((o) => o.ID === a.OptionID)?.OptionText ?? null;
  return a.AnswerText || null;
}
